import { Box, IconButton, Typography } from '@mui/material';
import MenuOutlined from '@mui/icons-material/MenuOutlined';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Whoami } from '../api/whoami';
import { resolveTrail } from './breadcrumb';
import type { NavItem } from './nav';
import { usePageHeaderState } from './pageHeader';

interface TopBarProps {
  navItems: NavItem[];
  whoami: Whoami | null;
  isMobile: boolean;
  onMenuClick: () => void;
}

/**
 * Top chrome (prototype `AdminTopBar`): a sticky bar carrying the route
 * breadcrumb, the big page title + subtitle, and the page's right-aligned
 * actions — all fed by the page-header store (each page declares them via
 * `<PageHeader/>`). The account menu lives at the sidebar bottom now, not here;
 * on mobile this bar also hosts the hamburger that opens the sidebar drawer.
 */
export function TopBar({ navItems, whoami, isMobile, onMenuClick }: TopBarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const header = usePageHeaderState();

  const customerName = whoami?.customer?.displayName ?? whoami?.customer?.name ?? t('app.name');
  const trailKeys = resolveTrail(navItems, location.pathname);
  const crumbs = [customerName, ...trailKeys.map((key) => t(key))];
  // Prefer the page's declared title; otherwise fall back to the last crumb so
  // the bar is never title-less.
  const title = header?.title ?? crumbs[crumbs.length - 1] ?? '';

  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        px: { xs: 2, md: '28px' },
        py: { xs: '12px', md: '14px' },
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {isMobile && (
        <IconButton
          edge="start"
          onClick={onMenuClick}
          aria-label={t('shell.openNavigation')}
          sx={{ flexShrink: 0 }}
        >
          <MenuOutlined />
        </IconButton>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          component="nav"
          aria-label="breadcrumb"
          sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
        >
          {crumbs.map((crumb, index) => {
            const last = index === crumbs.length - 1;
            return (
              <Box
                key={`${crumb}-${index}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                {index > 0 && (
                  <Box
                    component="span"
                    sx={{ color: 'text.secondary', opacity: 0.5, fontSize: 12 }}
                  >
                    /
                  </Box>
                )}
                <Typography
                  component="span"
                  sx={{
                    fontFamily: (theme) => theme.typography.button.fontFamily,
                    fontSize: 12,
                    lineHeight: 1.4,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    fontWeight: last ? 700 : 500,
                    color: last ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {crumb}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Typography
          component="h1"
          sx={{
            mt: '7px',
            fontFamily: (theme) => theme.typography.button.fontFamily,
            fontWeight: 700,
            fontSize: 23,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        {header?.subtitle && (
          <Typography variant="body2" sx={{ mt: '3px', fontWeight: 400, color: 'text.secondary' }}>
            {header.subtitle}
          </Typography>
        )}
      </Box>

      {header?.actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
          {header.actions}
        </Box>
      )}
    </Box>
  );
}
